import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

/** `system` no se persiste: es la ausencia de eleccion, no una eleccion mas. */
export type ThemePreference = Theme | 'system';

const STORAGE_KEY = 'liciya.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Modo claro/oscuro. El oscuro no redefine componentes: solo cambia el atributo
 * `data-theme` en <html>, y styles.scss re-apunta los alias semanticos.
 *
 * Mientras el usuario no elija, `preference` vale `system` y el tema sigue en
 * vivo a `prefers-color-scheme`. El primer clic fija la eleccion y la persiste.
 * Escribir en localStorage antes de que el usuario elija congelaria el tema en
 * el valor que tuviera el sistema esa primera visita.
 *
 * El primer pintado no lo decide este servicio sino el script inline de
 * index.html: la landing y el login se prerenderizan, asi que para cuando
 * Angular arranca la pagina ya se vio. Ambos leen la misma clave y la misma
 * media query.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  /** Lo que el usuario pidio. */
  readonly preference = signal<ThemePreference>(this.loadPreference());

  private readonly systemDark = signal(this.matchesSystemDark());

  /** Lo que se pinta. */
  readonly theme = computed<Theme>(() => {
    const preference = this.preference();
    if (preference !== 'system') return preference;
    return this.systemDark() ? 'dark' : 'light';
  });

  constructor() {
    this.watchSystem();

    effect(() => {
      const theme = this.theme();
      const preference = this.preference();
      if (!this.isBrowser) return;

      document.documentElement.setAttribute('data-theme', theme);

      if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, preference);
    });
  }

  /** Fija una eleccion explicita, opuesta a lo que se ve ahora. */
  toggle(): void {
    this.preference.set(this.theme() === 'dark' ? 'light' : 'dark');
  }

  set(value: ThemePreference): void {
    this.preference.set(value);
  }

  /** En SSR se asume `system`: el cliente rehidrata con la preferencia real. */
  private loadPreference(): ThemePreference {
    if (!this.isBrowser) return 'system';

    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  }

  private matchesSystemDark(): boolean {
    // matchMedia no existe en SSR ni en jsdom sin stub.
    if (!this.isBrowser) return false;
    return window.matchMedia?.(DARK_QUERY).matches ?? false;
  }

  /** Si el sistema cambia de tema mientras la app corre, se sigue en vivo. */
  private watchSystem(): void {
    if (!this.isBrowser) return;

    const query = window.matchMedia?.(DARK_QUERY);
    if (!query?.addEventListener) return;

    const onChange = (event: MediaQueryListEvent) => this.systemDark.set(event.matches);
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }
}
